import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dimmer, Loader, Message } from 'semantic-ui-react';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { orderBy } from '@progress/kendo-data-query';
import { toast } from 'react-semantic-toasts';
import { useComponentManager } from '../../providers/ComponentManagerProvider';
import { ManagePointsModal } from './ManagePointsModal';
import { MovePointModal } from './MovePointModal';
import { PointDeleteConfirmation } from './PointDeleteConfirmation';

const blank = value => (value == null || value === '' ? '' : value);

const PAGE_SIZE = 10;

// Flattens a Point for the grid. Orphaned = its generic_point row was deleted.
const toRow = p => ({
    id: p.id,
    orphaned: p.genericPoint == null,
    type: blank(p.genericPoint?.point_type),
    code: blank(p.genericPoint?.code),
    description: blank(p.genericPoint?.description),
    units: blank(p.genericPoint?.units),
    haystackTag: blank(p.genericPoint?.haystack_tag),
});

const orphanCell = field => props =>
    props.dataItem.orphaned ? (
        <td style={{ color: '#c0504d', fontWeight: 700 }}>Orphaned Data</td>
    ) : (
        <td>{props.dataItem[field]}</td>
    );

export const PointsTab = ({ componentId, onCountChange }) => {
    const { getComponentPoints, deleteComponentPoint, getPointDependents } = useComponentManager();

    const [points, setPoints] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const [sort, setSort] = useState([{ field: 'id', dir: 'asc' }]);
    const [page, setPage] = useState(0);

    const [manageOpen, setManageOpen] = useState(false);

    const [moveOpen, setMoveOpen] = useState(false);
    const [pendingMove, setPendingMove] = useState(null);

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [dependentCount, setDependentCount] = useState(0);

    // Guards against out-of-order loads when the component switches mid-flight.
    const requestToken = useRef(0);
    // Separate token for per-row dependent-count fetches (a quick second click
    // must not be overwritten by a slow earlier response).
    const dependentToken = useRef(0);

    // Hold the count callback in a ref so an unstable (inline) prop from a
    // caller can never churn `load` and re-trigger the reload effect in a loop.
    const onCountChangeRef = useRef(onCountChange);
    onCountChangeRef.current = onCountChange;
    const reportCount = n => {
        if (onCountChangeRef.current) onCountChangeRef.current(n);
    };

    const load = useCallback(() => {
        if (!componentId) return Promise.resolve();
        const token = (requestToken.current += 1);
        setLoading(true);
        setError(false);
        return getComponentPoints(componentId)
            .then(res => {
                if (token !== requestToken.current) return;
                const data = res?.data ?? [];
                setPoints(data);
                setLoading(false);
                reportCount(data.length);
            })
            .catch(() => {
                if (token !== requestToken.current) return;
                setError(true);
                setLoading(false);
                setPoints([]);
                reportCount(0);
            });
    }, [componentId, getComponentPoints]);

    useEffect(() => {
        setPage(0);
        setSort([{ field: 'id', dir: 'asc' }]);
        load();
    }, [componentId, load]);

    const rows = points.map(toRow);
    const sorted = orderBy(rows, sort);
    const paged = sorted.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

    const handleDeleteClick = async row => {
        const token = (dependentToken.current += 1);
        setPendingDelete(row.id);
        setDependentCount(0);
        setDeleteOpen(true);
        try {
            const res = await getPointDependents([row.id]);
            // Ignore if the user already moved on to another row's confirm.
            if (token !== dependentToken.current) return;
            setDependentCount(res?.count ?? 0);
        } catch {
            // Non-fatal: show the confirm without a count rather than blocking.
            if (token !== dependentToken.current) return;
            setDependentCount(0);
        }
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        try {
            await deleteComponentPoint(pendingDelete);
            await load();
            setDeleteOpen(false);
            setPendingDelete(null);
            toast({
                type: 'success',
                title: 'Success',
                description: 'Orphaned point deleted.',
                animation: 'fade down',
                time: 4000,
            });
        } catch (err) {
            toast({
                type: 'error',
                title: 'Error',
                description:
                    err?.response?.data?.message ||
                    'An error occurred while deleting the point.',
                animation: 'fade down',
                time: 8000,
            });
        } finally {
            setDeleting(false);
        }
    };

    if (error) {
        return (
            <Message negative data-testid="points-error">
                <Message.Header>Failed to load points</Message.Header>
                <Button basic size="small" onClick={load} style={{ marginTop: '0.5rem' }}>
                    Retry
                </Button>
            </Message>
        );
    }

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: '0.75rem',
                }}
            >
                <Button primary onClick={() => setManageOpen(true)} data-testid="manage-points-button">
                    Manage points
                </Button>
            </div>

            <div style={{ position: 'relative' }}>
                <Dimmer active={loading} inverted>
                    <Loader>Loading...</Loader>
                </Dimmer>
                {!loading && rows.length === 0 ? (
                    <div
                        style={{ textAlign: 'center', padding: '2rem', color: '#8c979c' }}
                        data-testid="points-empty"
                    >
                        <p>No points assigned to this component.</p>
                    </div>
                ) : (
                    <Grid
                        data={paged}
                        sortable={true}
                        sort={sort}
                        onSortChange={e => {
                            setSort(e.sort);
                            setPage(0);
                        }}
                        pageable={{ pageSizes: false }}
                        skip={page * PAGE_SIZE}
                        take={PAGE_SIZE}
                        total={rows.length}
                        onPageChange={e => setPage(e.page.skip / e.page.take)}
                    >
                        <Column field="id" title="Component Point #" />
                        <Column field="type" title="Type" cell={orphanCell('type')} />
                        <Column field="haystackTag" title="Haystack Tag" cell={orphanCell('haystackTag')} />
                        <Column field="code" title="Code" cell={orphanCell('code')} />
                        <Column field="description" title="Description" cell={orphanCell('description')} />
                        <Column field="units" title="Units" cell={orphanCell('units')} />
                        <Column
                            title="Actions"
                            sortable={false}
                            width="120px"
                            cell={props =>
                                props.dataItem.orphaned ? (
                                    <td>
                                        <Button
                                            size="small"
                                            basic
                                            className="orphan-delete-button"
                                            onClick={() => handleDeleteClick(props.dataItem)}
                                            data-testid={`point-delete-${props.dataItem.id}`}
                                        >
                                            Delete
                                        </Button>
                                    </td>
                                ) : (
                                    <td>
                                        <Button
                                            size="small"
                                            basic
                                            onClick={() => {
                                                setPendingMove(props.dataItem);
                                                setMoveOpen(true);
                                            }}
                                            data-testid={`point-move-${props.dataItem.id}`}
                                        >
                                            Move
                                        </Button>
                                    </td>
                                )
                            }
                        />
                    </Grid>
                )}
            </div>

            <ManagePointsModal
                open={manageOpen}
                setOpen={setManageOpen}
                componentId={componentId}
                // Orphaned points are cleared via the row Delete action, not here.
                currentPoints={points.filter(p => p.genericPoint != null)}
                orphanCount={points.filter(p => p.genericPoint == null).length}
                onSaved={load}
            />
            {pendingMove && (
                <MovePointModal
                    open={moveOpen}
                    setOpen={setMoveOpen}
                    point={pendingMove}
                    currentComponentId={componentId}
                    onMoved={load}
                />
            )}
            <PointDeleteConfirmation
                open={deleteOpen}
                setOpen={setDeleteOpen}
                submitAction={confirmDelete}
                loading={deleting}
                title="Delete this orphaned point?"
                confirmLabel="Delete"
                description={
                    <>
                        <p>
                            This point&rsquo;s definition no longer exists, leaving an orphaned
                            reference on this component. Deleting it clears that leftover
                            reference and cannot be undone.
                        </p>
                        {dependentCount > 0 && (
                            <Message info data-testid="dependents-info">
                                <p>
                                    {dependentCount} existing piece
                                    {dependentCount === 1 ? '' : 's'} of equipment still
                                    reference this point. They are not affected and keep their
                                    recorded data.
                                </p>
                            </Message>
                        )}
                    </>
                }
            />
        </div>
    );
};
